// The first-run coach on the live stage.
//
// The fight is driven by two gestures that nothing on screen announces — drag a unit to move it,
// right-click it for everything else — and by a rail of advisors whose plays are proposals rather than
// decisions. A new player who is not told those three things spends their first two minutes clicking
// the deck. So: three lines over the map, once per browser, gone on the first click.
//
// It is not a tour and it has no "next". Any click anywhere dismisses it, which is also the first
// click of the fight.

import { useEffect, useState } from 'react';

const LS_KEY = 'wt.coach.live.v1';

/** Has this browser already been shown the coach? A storage failure counts as "no". */
function alreadySeen(): boolean {
  try {
    return localStorage.getItem(LS_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(LS_KEY, '1');
  } catch {
    // Private window or storage denied: the coach shows again next time, which is harmless.
  }
}

const LINES: { key: string; head: string; body: string }[] = [
  { key: 'drag', head: 'Drag a unit', body: 'Press a friendly symbol and pull. The line, the route and the cost follow the cursor; let go to send it.' },
  { key: 'radial', head: 'Right-click for orders', body: 'Every other card is a wedge around the unit — fires, reconnaissance, resupply. Greyed means that unit cannot play it, and it says why.' },
  { key: 'advisors', head: 'Advisors propose — you decide', body: 'The three plays on the right are proposals with their evidence attached. Execute one, edit it first, or ignore it.' },
];

export function CoachOverlay() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!alreadySeen()) setOpen(true);
  }, []);

  if (!open) return null;

  const dismiss = (): void => {
    markSeen();
    setOpen(false);
  };

  return (
    <div
      data-testid="coach-overlay"
      // Full-bleed and pointer-transparent to the map underneath would swallow the dismissing click,
      // so the sheet itself takes the click and nothing else on the stage sees it.
      className="fade-in absolute inset-0 z-40 flex items-center justify-center"
      style={{ background: 'color-mix(in srgb, var(--color-ink) 72%, transparent)' }}
      onPointerDown={dismiss}
      onContextMenu={(e) => { e.preventDefault(); dismiss(); }}
    >
      <div className="wt-card max-w-[560px] px-5 py-4" style={{ background: 'var(--color-slate-900)' }}>
        <div className="mono text-[9px] uppercase tracking-[0.3em] text-fog">Three things, then it is your fight</div>
        <ul className="mt-3 space-y-2.5">
          {LINES.map((l) => (
            <li key={l.key} className="flex gap-3">
              <span
                className="mt-[5px] h-[7px] w-[7px] shrink-0"
                style={{ background: 'var(--color-friendly)' }}
                aria-hidden
              />
              <span className="min-w-0">
                <span className="block text-[13px] text-paper">{l.head}</span>
                <span className="block text-[11px] leading-snug text-fog">{l.body}</span>
              </span>
            </li>
          ))}
        </ul>
        <div className="mono mt-4 border-t border-slate-700 pt-2 text-[10px] uppercase tracking-[0.2em] text-fog">
          Click anywhere to begin
        </div>
      </div>
    </div>
  );
}
